import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, Send, Sparkles, ShieldCheck, Clock } from "lucide-react";

function useDarkMode() {
  const [dark, setDark] = useState(
    () => document.documentElement.getAttribute("data-theme") === "dark",
  );
  useEffect(() => {
    const observer = new MutationObserver(() =>
      setDark(document.documentElement.getAttribute("data-theme") === "dark"),
    );
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);
  return dark;
}

function useIsMobile(bp = 768) {
  const [m, setM] = useState(() => window.innerWidth < bp);
  useEffect(() => {
    const h = () => setM(window.innerWidth < bp);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [bp]);
  return m;
}

const CONTACT_INFO = [
  {
    icon: Mail,
    label: "Enquiry",
    value: "support@doxzon.com",
    href: "mailto:support@doxzon.com",
  },
  {
    icon: Phone,
    label: "Phone",
    value: "+254 712 XXX 678",
    href: "tel:+254 712 XXX 678",
  },
];

const GRADIENT_TEXT_CSS = `
  .dox-gradient-text {
    -webkit-background-clip: text !important;
    -webkit-text-fill-color: transparent !important;
    background-clip: text !important;
    color: transparent !important;
    display: inline !important;
  }
`;
if (
  typeof document !== "undefined" &&
  !document.getElementById("dox-gradient-text-style")
) {
  const tag = document.createElement("style");
  tag.id = "dox-gradient-text-style";
  tag.textContent = GRADIENT_TEXT_CSS;
  document.head.appendChild(tag);
}

function GradientText({ gradient, children }) {
  return (
    <span className="dox-gradient-text" style={{ backgroundImage: gradient }}>
      {children}
    </span>
  );
}

function Field({ label, type = "text", placeholder, dark, isMobile }) {
  return (
    <label style={{ display: "block" }}>
      <span
        style={{
          display: "block",
          marginBottom: 6,
          fontSize: isMobile ? 10 : 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          color: dark ? "#b89a4a" : "#64748b",
        }}
      >
        {label}
      </span>
      <input
        type={type}
        placeholder={placeholder}
        style={{
          width: "100%",
          borderRadius: 9999,
          border: `1px solid ${dark ? "rgba(200,150,30,0.30)" : "#e2e8f0"}`,
          background: dark ? "rgba(20,15,2,0.85)" : "#ffffff",
          padding: isMobile ? "10px 14px" : "12px 20px",
          fontSize: isMobile ? 13 : 14,
          color: dark ? "#f5e6b0" : "#334155",
          outline: "none",
          boxSizing: "border-box",
          transition: "all 0.3s",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = dark
            ? "rgba(240,192,64,0.70)"
            : "#22d3ee";
          e.target.style.boxShadow = dark
            ? "0 0 0 3px rgba(200,150,30,0.15)"
            : "0 0 0 3px rgba(34,211,238,0.12)";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = dark
            ? "rgba(200,150,30,0.30)"
            : "#e2e8f0";
          e.target.style.boxShadow = "none";
        }}
      />
    </label>
  );
}

export function Contact() {
  const dark = useDarkMode();
  const isMobile = useIsMobile();

  const t = dark
    ? {
        pageBg: "#0a0800",
        cardBg: "linear-gradient(135deg,#110d02 0%,#0f0a01 50%,#0d0c02 100%)",
        cardBorder: "rgba(200,150,30,0.22)",
        cardShadow:
          "0 20px 60px rgba(0,0,0,0.55), 0 0 40px rgba(200,150,30,0.06)",
        badgeBorder: "rgba(200,150,30,0.35)",
        badgeBg: "rgba(200,150,30,0.10)",
        badgeText: "#f0c040",
        headingColor: "#f5e6b0",
        accentGrad: "linear-gradient(90deg,#f0c040,#c8961e)",
        bodyText: "#b89a4a",
        contactBg: "rgba(20,14,2,0.80)",
        contactBorder: "rgba(200,150,30,0.20)",
        contactShadow: "0 2px 12px rgba(0,0,0,0.30)",
        contactHoverShadow: "0 8px 28px rgba(200,150,30,0.18)",
        iconGrad: "linear-gradient(135deg,#c8961e,#7a5c10)",
        iconShadow: "0 4px 14px rgba(200,150,30,0.35)",
        labelText: "#b89a4a",
        valueText: "#f5e6b0",
        pillBg: "rgba(20,14,2,0.80)",
        pillBorder: "rgba(200,150,30,0.22)",
        pillText: "#d4b060",
        formBg: "linear-gradient(135deg,#110d02,#0d0a01,#0f0b02)",
        formBorder: "rgba(200,150,30,0.22)",
        formShadow:
          "0 8px 32px rgba(0,0,0,0.40), 0 0 20px rgba(200,150,30,0.06)",
        textAreaBg: "rgba(20,15,2,0.85)",
        privacyText: "#7a5c10",
        btnBg: "linear-gradient(90deg,#3d2800 0%,#c8961e 100%)",
        btnShadow: "0 4px 18px rgba(200,150,30,0.35)",
      }
    : {
        pageBg: "#f7fbfc",
        cardBg: "linear-gradient(135deg,#eefaf7 0%,#f6fcfb 50%,#e9f7fb 100%)",
        cardBorder: "#e2e8f0",
        cardShadow: "0 20px 60px rgba(15,23,42,0.08)",
        badgeBorder: "#99f6e4",
        badgeBg: "#f0fdf9",
        badgeText: "#0d9488",
        headingColor: "#0f172a",
        accentGrad: "linear-gradient(90deg,#14b8a6,#06b6d4)",
        bodyText: "#64748b",
        contactBg: "rgba(255,255,255,0.80)",
        contactBorder: "#e2e8f0",
        contactShadow: "0 2px 8px rgba(15,23,42,0.06)",
        contactHoverShadow: "0 8px 24px rgba(15,23,42,0.12)",
        iconGrad: "linear-gradient(135deg,#22d3ee,#14b8a6)",
        iconShadow: "0 4px 14px rgba(20,184,166,0.35)",
        labelText: "#94a3b8",
        valueText: "#1e293b",
        pillBg: "#ffffff",
        pillBorder: "#e2e8f0",
        pillText: "#475569",
        formBg: "linear-gradient(135deg,#ffffff,#f8fcfc,#edf8fa)",
        formBorder: "#e2e8f0",
        formShadow: "0 8px 32px rgba(15,23,42,0.08)",
        textAreaBg: "#ffffff",
        privacyText: "#94a3b8",
        btnBg: "linear-gradient(90deg,#042b49 0%,#00c7a5 100%)",
        btnShadow: "0 4px 18px rgba(0,199,165,0.30)",
      };

  return (
    <section
      id="contact"
      style={{
        background: t.pageBg,
        padding: isMobile ? "3rem 0 4rem" : "6rem 0",
        transition: "background 0.4s",
        overflowX: "hidden",
      }}
    >
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: isMobile ? "0 16px" : "0 1.5rem",
          boxSizing: "border-box",
          width: "100%",
        }}
      >
        <div
          style={{
            position: "relative",
            overflow: "hidden",
            borderRadius: isMobile ? 24 : 40,
            border: `1px solid ${t.cardBorder}`,
            background: t.cardBg,
            boxShadow: t.cardShadow,
            /* KEY: mobile = single column (row), desktop = two columns */
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1.1fr",
            gap: isMobile ? 24 : 40,
            padding: isMobile ? 20 : 40,
            transition: "background 0.4s, border 0.4s",
          }}
        >
          {/* ── Left side ── */}
          <div style={{ minWidth: 0 }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                borderRadius: 9999,
                border: `1px solid ${t.badgeBorder}`,
                background: t.badgeBg,
                padding: isMobile ? "5px 12px" : "6px 16px",
                fontSize: isMobile ? 9 : 10,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                color: t.badgeText,
              }}
            >
              <Sparkles size={isMobile ? 10 : 12} color={t.badgeText} />
              Join Doxzon Network
            </span>

            <h2
              style={{
                marginTop: isMobile ? 14 : 24,
                fontSize: isMobile ? "1.5rem" : "clamp(1.8rem,3vw,2.5rem)",
                fontWeight: 700,
                lineHeight: 1.2,
                color: t.headingColor,
                wordBreak: "break-word",
              }}
            >
              Tell us how{" "}
              <GradientText gradient={t.accentGrad}>we</GradientText> can help.
            </h2>

            <p
              style={{
                marginTop: isMobile ? 10 : 20,
                fontSize: isMobile ? 13 : 16,
                lineHeight: 1.65,
                color: t.bodyText,
              }}
            >
              Let's create better healthcare experience together.
            </p>

            {/* Contact info cards */}
            <div
              style={{
                marginTop: isMobile ? 20 : 36,
                display: "flex",
                flexDirection: "column",
                gap: isMobile ? 8 : 12,
              }}
            >
              {CONTACT_INFO.map((item) => (
                <a
                  key={item.label}
                  href={item.href || "#"}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: isMobile ? 12 : 16,
                    borderRadius: isMobile ? 16 : 24,
                    border: `1px solid ${t.contactBorder}`,
                    background: t.contactBg,
                    padding: isMobile ? "12px" : "16px",
                    boxShadow: t.contactShadow,
                    textDecoration: "none",
                    transition: "all 0.3s",
                    minWidth: 0,
                    overflow: "hidden",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-3px)";
                    e.currentTarget.style.boxShadow = t.contactHoverShadow;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = t.contactShadow;
                  }}
                >
                  <div
                    style={{
                      width: isMobile ? 36 : 48,
                      height: isMobile ? 36 : 48,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      background: t.iconGrad,
                      boxShadow: t.iconShadow,
                    }}
                  >
                    <item.icon size={isMobile ? 16 : 20} color="#fff" />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <p
                      style={{
                        margin: 0,
                        fontSize: isMobile ? 9 : 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                        color: t.labelText,
                      }}
                    >
                      {item.label}
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontWeight: 600,
                        fontSize: isMobile ? 12 : 14,
                        color: t.valueText,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.value}
                    </p>
                  </div>
                </a>
              ))}
            </div>

            {/* Pill badges */}
            <div
              style={{
                marginTop: isMobile ? 16 : 28,
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              {[
                { icon: ShieldCheck, text: "HIPAA Compliant" },
                { icon: Clock, text: "Reply in 4 hrs" },
              ].map(({ icon: Icon, text }) => (
                <span
                  key={text}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    borderRadius: 9999,
                    border: `1px solid ${t.pillBorder}`,
                    background: t.pillBg,
                    padding: isMobile ? "6px 12px" : "8px 16px",
                    fontSize: isMobile ? 11 : 12,
                    color: t.pillText,
                    boxShadow: t.contactShadow,
                  }}
                >
                  <Icon size={12} color={t.badgeText} />
                  {text}
                </span>
              ))}
            </div>
          </div>

          {/* ── Right side — Form ── */}
          <motion.form
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            onSubmit={(e) => e.preventDefault()}
            style={{
              borderRadius: isMobile ? 18 : 34,
              border: `1px solid ${t.formBorder}`,
              background: t.formBg,
              padding: isMobile ? 16 : 32,
              boxShadow: t.formShadow,
            }}
          >
            {/* Fields grid — 2 cols on desktop, 1 col on mobile */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: isMobile ? 12 : 20,
              }}
            >
              <Field
                label="Name"
                placeholder="Jane Doe"
                dark={dark}
                isMobile={isMobile}
              />
              <Field
                label="Email"
                type="email"
                placeholder="Enter your mail id"
                dark={dark}
                isMobile={isMobile}
              />
              <Field
                label="Phone"
                placeholder="Enter your phone number"
                dark={dark}
                isMobile={isMobile}
              />
              <Field
                label="Subject"
                placeholder=""
                dark={dark}
                isMobile={isMobile}
              />
            </div>

            <label style={{ marginTop: isMobile ? 12 : 20, display: "block" }}>
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 6,
                  fontSize: isMobile ? 10 : 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: dark ? "#b89a4a" : "#64748b",
                }}
              >
                <span>Message</span>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 500,
                    textTransform: "none",
                    letterSpacing: "normal",
                    color: dark ? "#7a5c10" : "#94a3b8",
                  }}
                >
                  max 500 chars
                </span>
              </span>
              <textarea
                rows={isMobile ? 4 : 7}
                maxLength={500}
                style={{
                  width: "100%",
                  resize: "none",
                  borderRadius: isMobile ? 16 : 28,
                  border: `1px solid ${dark ? "rgba(200,150,30,0.30)" : "#e2e8f0"}`,
                  background: t.textAreaBg,
                  padding: isMobile ? "10px 14px" : "16px 20px",
                  fontSize: isMobile ? 13 : 14,
                  color: dark ? "#f5e6b0" : "#334155",
                  outline: "none",
                  boxSizing: "border-box",
                  height: isMobile ? 100 : 160,
                  fontFamily: "inherit",
                  transition: "all 0.3s",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = dark
                    ? "rgba(240,192,64,0.70)"
                    : "#22d3ee";
                  e.target.style.boxShadow = dark
                    ? "0 0 0 3px rgba(200,150,30,0.15)"
                    : "0 0 0 3px rgba(34,211,238,0.12)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = dark
                    ? "rgba(200,150,30,0.30)"
                    : "#e2e8f0";
                  e.target.style.boxShadow = "none";
                }}
              />
            </label>

            <div
              style={{
                marginTop: isMobile ? 16 : 28,
                display: "flex",
                alignItems: isMobile ? "flex-start" : "center",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "space-between",
                gap: isMobile ? 12 : 16,
              }}
            >
              <p
                style={{
                  fontSize: isMobile ? 11 : 12,
                  color: t.privacyText,
                  margin: 0,
                }}
              >
                By submitting you agree to our privacy policy.
              </p>
              <button
                type="submit"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  borderRadius: 9999,
                  padding: isMobile ? "12px 24px" : "14px 32px",
                  background: t.btnBg,
                  color: "#fff",
                  border: "none",
                  fontSize: isMobile ? 13 : 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  boxShadow: t.btnShadow,
                  width: isMobile ? "100%" : "auto",
                  justifyContent: isMobile ? "center" : "flex-start",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.04)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                Connect with us
                <Send size={isMobile ? 14 : 16} />
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
